import { validateAndParseCSV } from 'components/DataTransferForm/helpers/csvValidator';
import type {
  CSVParseConfig,
  ImportResult,
} from 'components/DataTransferForm/types';
import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useNotifications } from 'utils/hooks/useNotifications';
import type {
  AimApplicationDraft,
  AimFlowDraft,
  AimWorkloadDraft,
} from '../../../model/aimDrafts';
import { AimDataTransferForm, type ImportSummary } from './AimDataTransferForm';
import {
  buildAimApplicationsCsvConfig,
  buildAimFlowsCsvConfig,
  buildAimWorkloadsCsvConfig,
} from './aimCsvSchemas';
import { downloadAimExampleFiles } from './aimExampleData';
import type { AimEntityKind } from './types';

type AimFormShape = {
  aimApplications: AimApplicationDraft[];
  aimWorkloads: AimWorkloadDraft[];
  aimFlows: AimFlowDraft[];
};

type FileSlots = {
  applicationsFile: File | null;
  workloadsFile: File | null;
  flowsFile: File | null;
};

type Props = {
  triggeredFrom: AimEntityKind;
  form: UseFormReturn<AimFormShape>;
  onClose: () => void;
};

// ---------------------------------------------------------------------------
// Helper: parse a single CSV file with the given config.
// Returns null when no file was provided (slot empty).
// ---------------------------------------------------------------------------
const parseEntity = async <T,>(
  file: File | null,
  config: CSVParseConfig<T>,
): Promise<ImportResult<T> | null> => {
  if (!file) {
    return null;
  }
  const res = await validateAndParseCSV(file, config);
  if (!res.isValid || !res.result) {
    return {
      validData: [],
      errors: [{ id: 'File error', message: res.error ?? 'Parse failed' }],
    };
  }
  return res.result;
};

// ---------------------------------------------------------------------------
// Helper: apply parse result to form, guarded by all-fail safety.
// Zero valid rows → do NOT mutate (keeps existing data even in replace mode).
// ---------------------------------------------------------------------------
const applyToForm = <T,>(
  result: ImportResult<T> | null,
  current: T[],
  isReplace: boolean,
  setValue: (value: T[], opts: { shouldDirty: boolean }) => void,
): void => {
  if (!result || result.validData.length === 0) {
    return;
  }
  const next = isReplace ? result.validData : [...current, ...result.validData];
  setValue(next, { shouldDirty: true });
};

// ---------------------------------------------------------------------------
// Helper: build the ImportSummary from per-entity results.
// ---------------------------------------------------------------------------
const buildSummary = (
  appsResult: ImportResult<AimApplicationDraft> | null,
  wlsResult: ImportResult<AimWorkloadDraft> | null,
  flowsResult: ImportResult<AimFlowDraft> | null,
): ImportSummary => ({
  perEntity: {
    ...(appsResult && {
      applications: {
        imported: appsResult.validData.length,
        errors: appsResult.errors,
      },
    }),
    ...(wlsResult && {
      workloads: {
        imported: wlsResult.validData.length,
        errors: wlsResult.errors,
      },
    }),
    ...(flowsResult && {
      flows: {
        imported: flowsResult.validData.length,
        errors: flowsResult.errors,
      },
    }),
  },
  hasAnyImport:
    (appsResult?.validData.length ?? 0) > 0 ||
    (wlsResult?.validData.length ?? 0) > 0 ||
    (flowsResult?.validData.length ?? 0) > 0,
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export const AimDataTransferManager = ({
  triggeredFrom,
  form,
  onClose,
}: Props) => {
  const notifications = useNotifications();

  const handleImport = useCallback(
    async (
      slots: FileSlots,
      mode: 'append' | 'replace',
    ): Promise<ImportSummary> => {
      const isReplace = mode === 'replace';
      const current = {
        applications: form.getValues('aimApplications'),
        workloads: form.getValues('aimWorkloads'),
        flows: form.getValues('aimFlows'),
      };

      // -- Step 1: parse uploaded files with mode-aware configs ------------
      const appsResult = await parseEntity<AimApplicationDraft>(
        slots.applicationsFile,
        buildAimApplicationsCsvConfig({
          isReplaceMode: isReplace,
          existingIds: new Set(current.applications.map((a) => a.id)),
        }),
      );
      const wlsResult = await parseEntity<AimWorkloadDraft>(
        slots.workloadsFile,
        buildAimWorkloadsCsvConfig({
          isReplaceMode: isReplace,
          existingIds: new Set(current.workloads.map((w) => w.id)),
        }),
      );
      const flowsResult = await parseEntity<AimFlowDraft>(
        slots.flowsFile,
        buildAimFlowsCsvConfig({
          isReplaceMode: isReplace,
          existingIds: new Set(current.flows.map((f) => f.id)),
        }),
      );

      // -- Step 2: apply to form state with all-fail safety guard ----------
      applyToForm(appsResult, current.applications, isReplace, (v, o) =>
        form.setValue('aimApplications', v, o),
      );
      applyToForm(wlsResult, current.workloads, isReplace, (v, o) =>
        form.setValue('aimWorkloads', v, o),
      );
      applyToForm(flowsResult, current.flows, isReplace, (v, o) =>
        form.setValue('aimFlows', v, o),
      );

      // -- Step 3: build summary -------------------------------------------
      const summary = buildSummary(appsResult, wlsResult, flowsResult);

      // -- Step 4: terse toast only on hard failure ------------------------
      if (!summary.hasAnyImport) {
        notifications.showNotification({
          type: 'warning',
          message:
            'Import: no rows imported. Review the result panel for details.',
        });
      }

      return summary;
    },
    [form],
  );

  return (
    <AimDataTransferForm
      triggeredFrom={triggeredFrom}
      onDownloadExample={downloadAimExampleFiles}
      onImport={handleImport}
      onClose={onClose}
    />
  );
};
